import React, { useState, useEffect, useRef, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../utils/Api";
import { AuthContext } from "../../context/AuthContext";
import { jwtDecode } from "jwt-decode";
import { Eye, EyeOff, ChevronLeft, Check, Upload } from "lucide-react";

const underlineInput = (hasError, extra = {}) => ({
  width: "100%",
  padding: "6px 4px",
  background: "transparent",
  border: "none",
  borderBottom: `2px solid ${hasError ? "#EF4444" : "#E2E8F0"}`,
  borderRadius: 0,
  fontSize: "14px",
  color: "#0F172A",
  outline: "none",
  fontFamily: "inherit",
  transition: "border-color 0.15s",
  boxSizing: "border-box",
  ...extra,
});

const UInput = ({ hasError, style: extraStyle, ...props }) => (
  <input
    {...props}
    style={underlineInput(hasError, extraStyle)}
    onFocus={(e) => { if (!hasError) e.target.style.borderBottomColor = "#FF7A00"; props.onFocus?.(e); }}
    onBlur={(e)  => { e.target.style.borderBottomColor = hasError ? "#EF4444" : "#E2E8F0"; props.onBlur?.(e); }}
  />
);

const FieldError = ({ msg }) => msg ? (
  <p style={{ fontSize: "11px", color: "#EF4444", margin: "4px 0 0", fontWeight: "500" }}>{msg}</p>
) : null;

function RegisterForm() {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [step, setStep] = useState(1);
  const [errE, setErrE] = useState({});
  const [errU, setErrU] = useState({});
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");

  // ── Efecto typewriter ────────────────────────────────────────────────────
  const TW_LINES = ["Únete a", "Turnora!"];
  const [typed, setTyped]         = useState(["", ""]);
  const [showCursor, setShowCursor] = useState(true);
  const lineRef = useRef(0);
  const charRef = useRef(0);

  useEffect(() => {
    let timeout;
    const type = () => {
      const l = lineRef.current;
      const c = charRef.current;
      if (l >= TW_LINES.length) {
        const blink = setInterval(() => setShowCursor(p => !p), 530);
        return () => clearInterval(blink);
      }
      if (c < TW_LINES[l].length) {
        setTyped(prev => {
          const next = [...prev];
          next[l] = TW_LINES[l].slice(0, c + 1);
          return next;
        });
        charRef.current++;
        timeout = setTimeout(type, 70);
      } else {
        lineRef.current++;
        charRef.current = 0;
        timeout = setTimeout(type, 600);
      }
    };
    timeout = setTimeout(type, 300);
    return () => clearTimeout(timeout);
  }, []); // eslint-disable-line

  const [emp, setEmp] = useState({
    nombre_fiscal: "", cif: "", direccion: "", telefono: "", email: "", logo: null,
  });

  const [usr, setUsr] = useState({
    dni: "", nombre: "", apellidos: "", email: "", telefono: "",
    rol: "admin", estado: "activo", password: "", password_confirmation: "",
  });

  const valEmp = () => {
    const e = {};
    if (!emp.nombre_fiscal.trim()) e.nombre_fiscal = "Obligatorio.";
    if (!emp.cif.trim()) e.cif = "Obligatorio.";
    else if (!/^[A-Za-z0-9][0-9]{7}[A-Za-z0-9]$/.test(emp.cif)) e.cif = "Formato: B12345678 (letra, 7 dígitos, letra/número).";
    if (!emp.direccion.trim()) e.direccion = "Obligatorio.";
    if (!emp.telefono.trim()) e.telefono = "Obligatorio.";
    else if (!/^\d{9}$/.test(emp.telefono)) e.telefono = "9 dígitos.";
    if (!emp.email.trim()) e.email = "Obligatorio.";
    else if (!/\S+@\S+\.\S+/.test(emp.email)) e.email = "Email inválido.";
    return e;
  };

  const valUsr = () => {
    const e = {};
    if (!usr.dni.trim()) e.dni = "Obligatorio.";
    else if (!/^\d{8}[A-Z]$/.test(usr.dni)) e.dni = "Formato: 12345678A.";
    if (!usr.nombre.trim()) e.nombre = "Obligatorio.";
    if (!usr.apellidos.trim()) e.apellidos = "Obligatorio.";
    if (!usr.email.trim()) e.email = "Obligatorio.";
    else if (!/\S+@\S+\.\S+/.test(usr.email)) e.email = "Email inválido.";
    if (!usr.telefono.trim()) e.telefono = "Obligatorio.";
    else if (!/^\d{9}$/.test(usr.telefono)) e.telefono = "9 dígitos.";
    if (!usr.password.trim()) e.password = "Obligatorio.";
    else if (usr.password.length < 8) e.password = "Mínimo 8 caracteres.";
    if (usr.password !== usr.password_confirmation) e.password_confirmation = "No coinciden.";
    return e;
  };

  const nextStep = (e) => {
    e.preventDefault();
    const errs = valEmp();
    setErrE(errs);
    if (!Object.keys(errs).length) setStep(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = valUsr();
    setErrU(errs);
    if (Object.keys(errs).length) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("nombre_fiscal", emp.nombre_fiscal);
      fd.append("cif", emp.cif);
      fd.append("direccion", emp.direccion);
      fd.append("telefono_empresa", emp.telefono);
      fd.append("email_empresa", emp.email);
      if (emp.logo) fd.append("logo", emp.logo);
      fd.append("dni", usr.dni);
      fd.append("nombre", usr.nombre);
      fd.append("apellidos", usr.apellidos);
      fd.append("email_usuario", usr.email);
      fd.append("telefono_usuario", usr.telefono);
      fd.append("rol", usr.rol);
      fd.append("estado", usr.estado);
      fd.append("password", usr.password);
      fd.append("password_confirmation", usr.password_confirmation);
      const res = await api.post("/register", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { token } = res.data;
      setSuccess("¡Cuenta creada! Redirigiendo…");
      if (token && login(token)) {
        const decoded = jwtDecode(token);
        setTimeout(() => navigate(decoded.rol === "admin" ? "/admin" : "/empleados"), 1200);
      } else {
        setTimeout(() => navigate("/"), 2500);
      }
    } catch (err) {
      const ve = err.isValidationError ? err.errors : err.response?.data?.errors;
      if (ve) {
        const empErrs = {};
        if (ve.nombre_fiscal) empErrs.nombre_fiscal = ve.nombre_fiscal[0];
        if (ve.cif) empErrs.cif = ve.cif[0];
        if (ve.email_empresa) empErrs.email = ve.email_empresa[0];
        setErrE(empErrs);
        setErrU({ dni: ve.dni?.[0], email: ve.email_usuario?.[0] });
        if (Object.keys(empErrs).length) setStep(1);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", fontFamily: "'Inter', system-ui, sans-serif" }}>

      {/* ── Panel izquierdo ───────────────────────────────────────── */}
      <div className="tn-auth-left" style={{
        flex: "0 0 44%",
        background: "linear-gradient(145deg, #0F1629 0%, #1A2347 50%, #1E3A8A 100%)",
        display: "flex",
        flexDirection: "column",
        padding: "40px 48px",
        position: "relative",
        overflow: "hidden",
      }}>
        {/* Patrón geométrico */}
        <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.07 }} xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="diagR" x="0" y="0" width="120" height="120" patternUnits="userSpaceOnUse" patternTransform="rotate(25)">
              <line x1="0"  y1="0" x2="0"  y2="120" stroke="white" strokeWidth="1"/>
              <line x1="40" y1="0" x2="40" y2="120" stroke="white" strokeWidth="1"/>
              <line x1="80" y1="0" x2="80" y2="120" stroke="white" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#diagR)" />
        </svg>
        
        {/* Glow naranja */}
        <div style={{
          position: "absolute", bottom: "-60px", left: "-60px",
          width: "280px", height: "280px",
          background: "radial-gradient(circle, rgba(255,122,0,0.18) 0%, transparent 70%)",
          pointerEvents: "none"
        }} />

        {/* Icono asterisco */}
        <div style={{ position: "relative", zIndex: 1, marginBottom: "auto" }}>
          <svg width="56" height="56" viewBox="0 0 64 64" fill="none" style={{ marginBottom: "40px" }}>
            <line x1="32" y1="4"  x2="32" y2="60" stroke="white" strokeWidth="7" strokeLinecap="round"/>
            <line x1="4"  y1="32" x2="60" y2="32" stroke="white" strokeWidth="7" strokeLinecap="round"/>
            <line x1="11" y1="11" x2="53" y2="53" stroke="white" strokeWidth="7" strokeLinecap="round"/>
            <line x1="53" y1="11" x2="11" y2="53" stroke="white" strokeWidth="7" strokeLinecap="round"/>
          </svg>

          <h2 style={{
            fontSize: "clamp(30px, 3.5vw, 46px)",
            fontWeight: "800",
            color: "#FFFFFF",
            lineHeight: "1.1",
            letterSpacing: "-1.5px",
            margin: "0 0 16px",
          }}>
            {typed[0]}<br />
            <span style={{ color: "#FF7A00" }}>
              {typed[1]}
              <span style={{
                display: "inline-block",
                width: "3px",
                height: "0.85em",
                background: "#FF7A00",
                marginLeft: "3px",
                verticalAlign: "middle",
                borderRadius: "1px",
                opacity: showCursor ? 1 : 0,
                transition: "opacity 0.1s",
              }} />
            </span>
          </h2>

          <p style={{
            fontSize: "15px",
            color: "rgba(255,255,255,0.55)",
            lineHeight: "1.7",
            maxWidth: "300px",
            margin: 0,
          }}>
            Crea tu empresa en 2 minutos y empieza a gestionar turnos, fichajes y ausencias de forma automática.
          </p>

          {/* Steps preview */}
          <div style={{ marginTop: "28px", display: "flex", flexDirection: "column", gap: "10px" }}>
            {[
              { n: "01", t: "Datos de tu empresa" },
              { n: "02", t: "Tu cuenta de administrador" },
            ].map((s) => (
              <div key={s.n} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <span style={{
                  fontSize: "11px", fontWeight: "700", color: step > parseInt(s.n) ? "#FF7A00" : "rgba(255,255,255,0.3)",
                  letterSpacing: "1px", minWidth: "24px"
                }}>{s.n}</span>
                <span style={{ fontSize: "13px", color: step >= parseInt(s.n) ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.3)", fontWeight: "500" }}>
                  {s.t}
                </span>
                {step > parseInt(s.n) && <Check size={14} style={{ color: "#10B981", marginLeft: "auto" }} />}
              </div>
            ))}
          </div>
        </div>

        <p style={{ position: "relative", zIndex: 1, fontSize: "12px", color: "rgba(255,255,255,0.25)", margin: 0, marginTop: "40px" }}>
          © 2026 Turnora. Todos los derechos reservados.
        </p>
      </div>

      {/* ── Panel derecho ─────────────────────────────────────────── */}
      <div style={{
        flex: 1,
        background: "#FFFFFF",
        display: "flex",
        flexDirection: "column",
        padding: "28px 52px 24px",
        overflow: "hidden",
      }}>
        {/* Brand */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px", flexShrink: 0 }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="#FF7A00" className="bi bi-clock-history me-2 align-middle" viewBox="0 0 16 16"><path d="M8.515 1.019A7 7 0 1 0 15 8a.5.5 0 0 0-1 0 6 6 0 1 1-5.485-6.981z"></path><path d="M7.5 3a.5.5 0 0 1 .5.5v4.25l3.15 1.85a.5.5 0 1 1-.5.866L7.5 8.5V3.5A.5.5 0 0 1 7.5 3z"></path></svg>
          <span style={{ fontSize: "16px", fontWeight: "700", color: "#0F172A", letterSpacing: "-0.3px" }}>Turnora</span>
        </div>

        <div style={{ maxWidth: "420px", width: "100%", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>

          {/* Stepper top */}
          <div style={{ display: "flex", alignItems: "center", gap: "0", marginBottom: "20px" }}>
            {[{ num: 1, label: "Empresa" }, { num: 2, label: "Usuario" }].map((s, i, arr) => (
              <React.Fragment key={s.num}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <div style={{
                    width: "26px", height: "26px", borderRadius: "50%",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "11px", fontWeight: "700",
                    background: step > s.num ? "#10B981" : step === s.num ? "#0F172A" : "#F1F5F9",
                    color: step >= s.num ? "white" : "#94A3B8",
                    transition: "all 0.2s",
                  }}>
                    {step > s.num ? <Check size={13} /> : s.num}
                  </div>
                  <span style={{ fontSize: "13px", fontWeight: "600", color: step >= s.num ? "#0F172A" : "#94A3B8" }}>
                    {s.label}
                  </span>
                </div>
                {i < arr.length - 1 && (
                  <div style={{
                    flex: 1, height: "2px", margin: "0 12px",
                    background: step > 1 ? "#10B981" : "#E2E8F0",
                    transition: "background 0.3s",
                  }} />
                )}
              </React.Fragment>
            ))}
          </div>

          {success && (
            <div style={{
              display: "flex", alignItems: "center", gap: "10px",
              padding: "12px 14px", marginBottom: "24px",
              background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: "8px"
            }}>
              <Check size={16} style={{ color: "#10B981" }} />
              <span style={{ fontSize: "13px", color: "#15803D", fontWeight: "500" }}>{success}</span>
            </div>
          )}

          {/* ── STEP 1 ── */}
          {step === 1 && (
            <>
              <div style={{ marginBottom: "16px" }}>
                <h1 style={{ fontSize: "24px", fontWeight: "800", color: "#0F172A", margin: "0 0 6px", letterSpacing: "-0.8px" }}>
                  Tu empresa
                </h1>
                <p style={{ fontSize: "13px", color: "#64748B", margin: 0 }}>
                  ¿Ya tienes cuenta?{" "}
                  <Link to="/" style={{ color: "#FF7A00", fontWeight: "600", textDecoration: "underline" }}>
                    Inicia sesión aquí
                  </Link>
                </p>
              </div>

              <form onSubmit={nextStep} noValidate>
                <div style={{ marginBottom: "14px" }}>
                  <label style={{ fontSize: "11px", fontWeight: "700", color: "#94A3B8", letterSpacing: "0.8px", textTransform: "uppercase" }}>
                    Nombre legal
                  </label>
                  <UInput
                    type="text" hasError={!!errE.nombre_fiscal}
                    value={emp.nombre_fiscal}
                    onChange={(e) => setEmp({ ...emp, nombre_fiscal: e.target.value })}
                    placeholder="Turnora S.L."
                  />
                  <FieldError msg={errE.nombre_fiscal} />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "14px" }}>
                  <div>
                    <label style={{ fontSize: "11px", fontWeight: "700", color: "#94A3B8", letterSpacing: "0.8px", textTransform: "uppercase" }}>CIF</label>
                    <UInput type="text" hasError={!!errE.cif} 
                      value={emp.cif}
                      onChange={(e) => setEmp({ ...emp, cif: e.target.value })} 
                      placeholder="B12345678"
                      maxLength={9}
                    />
                    <FieldError msg={errE.cif} />
                  </div>
                  <div>
                    <label style={{ fontSize: "11px", fontWeight: "700", color: "#94A3B8", letterSpacing: "0.8px", textTransform: "uppercase" }}>Teléfono</label>
                    <UInput type="tel" 
                      hasError={!!errE.telefono} 
                      value={emp.telefono}
                      onChange={(e) => setEmp({ ...emp, telefono: e.target.value })} 
                      placeholder="958 000 000"
                      maxLength={9}  
                      />
                    <FieldError msg={errE.telefono} />
                  </div>
                </div>

                <div style={{ marginBottom: "14px" }}>
                  <label style={{ fontSize: "11px", fontWeight: "700", color: "#94A3B8", letterSpacing: "0.8px", textTransform: "uppercase" }}>Dirección</label>
                  <UInput type="text" hasError={!!errE.direccion} value={emp.direccion}
                    onChange={(e) => setEmp({ ...emp, direccion: e.target.value })} placeholder="Calle Altillo" />
                  <FieldError msg={errE.direccion} />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "24px" }}>
                  <div>
                    <label style={{ fontSize: "11px", fontWeight: "700", color: "#94A3B8", letterSpacing: "0.8px", textTransform: "uppercase" }}>Email</label>
                    <UInput type="email" hasError={!!errE.email} value={emp.email}
                      onChange={(e) => setEmp({ ...emp, email: e.target.value })} placeholder="hola@turnora.es" />
                    <FieldError msg={errE.email} />
                  </div>
                  <div>
                    <label style={{ fontSize: "11px", fontWeight: "700", color: "#FF7A00", letterSpacing: "0.8px", textTransform: "uppercase" }}>
                      Logo (opcional)
                    </label>
                    <label style={{
                      display: "flex", alignItems: "center", gap: "6px",
                      padding: "8px 4px",
                      borderBottom: "2px solid #E2E8F0",
                      cursor: "pointer", fontSize: "13px", color: "#64748B"
                    }}>
                      <input type="file" accept="image/*"
                        onChange={(e) => setEmp({ ...emp, logo: e.target.files[0] })}
                        style={{ display: "none" }} />
                      <Upload size={14} style={{ color: "#FF7A00" }} />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "12px" }}>
                        {emp.logo ? emp.logo.name : "Subir archivo"}
                      </span>
                    </label>
                  </div>
                </div>

                <button type="submit" style={{
                  width: "100%", height: "46px",
                  background: "#0F172A", color: "white",
                  border: "none", borderRadius: "10px",
                  fontSize: "14px", fontWeight: "700",
                  cursor: "pointer", fontFamily: "inherit",
                  transition: "background 0.15s", letterSpacing: "-0.2px"
                }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "#1E2947"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "#0F172A"}
                >
                  Siguiente →
                </button>
              </form>
            </>
          )}

          {/* ── STEP 2 ── */}
          {step === 2 && (
            <>
              <div style={{ marginBottom: "16px" }}>
                <h1 style={{ fontSize: "24px", fontWeight: "800", color: "#0F172A", margin: "0 0 6px", letterSpacing: "-0.8px" }}>
                  Tu cuenta
                </h1>
                <p style={{ fontSize: "13px", color: "#64748B", margin: 0 }}>
                  Datos del administrador principal
                </p>
              </div>

              <form onSubmit={submit} noValidate>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "14px" }}>
                  <div>
                    <label style={{ fontSize: "11px", fontWeight: "700", color: "#94A3B8", letterSpacing: "0.8px", textTransform: "uppercase" }}>Nombre</label>
                    <UInput type="text" hasError={!!errU.nombre} value={usr.nombre}
                      onChange={(e) => setUsr({ ...usr, nombre: e.target.value })} placeholder="Pedro" />
                    <FieldError msg={errU.nombre} />
                  </div>
                  <div>
                    <label style={{ fontSize: "11px", fontWeight: "700", color: "#94A3B8", letterSpacing: "0.8px", textTransform: "uppercase" }}>Apellidos</label>
                    <UInput type="text" hasError={!!errU.apellidos} value={usr.apellidos}
                      onChange={(e) => setUsr({ ...usr, apellidos: e.target.value })} placeholder="García López" />
                    <FieldError msg={errU.apellidos} />
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "14px" }}>
                  <div>
                    <label style={{ fontSize: "11px", fontWeight: "700", color: "#94A3B8", letterSpacing: "0.8px", textTransform: "uppercase" }}>DNI</label>
                    <UInput type="text" hasError={!!errU.dni} 
                      value={usr.dni}
                      onChange={(e) => setUsr({ ...usr, dni: e.target.value })} 
                      placeholder="12345678A"
                      maxLength={9}
                      />
                    <FieldError msg={errU.dni} />
                  </div>
                  <div>
                    <label style={{ fontSize: "11px", fontWeight: "700", color: "#94A3B8", letterSpacing: "0.8px", textTransform: "uppercase" }}>Teléfono</label>
                    <UInput type="tel" 
                      hasError={!!errU.telefono} value={usr.telefono}
                      onChange={(e) => setUsr({ ...usr, telefono: e.target.value })} 
                      placeholder="958 000 000" 
                      maxLength={9}/>
                    <FieldError msg={errU.telefono} />
                  </div>
                </div>

                <div style={{ marginBottom: "14px" }}>
                  <label style={{ fontSize: "11px", fontWeight: "700", color: "#94A3B8", letterSpacing: "0.8px", textTransform: "uppercase" }}>Email</label>
                  <UInput type="email" hasError={!!errU.email} value={usr.email}
                    onChange={(e) => setUsr({ ...usr, email: e.target.value })} placeholder="hola@turnora.es" />
                  <FieldError msg={errU.email} />
                </div>

                <div style={{ marginBottom: "14px" }}>
                  <label style={{ fontSize: "11px", fontWeight: "700", color: "#94A3B8", letterSpacing: "0.8px", textTransform: "uppercase" }}>Contraseña</label>
                  <div style={{ position: "relative" }}>
                    <UInput
                      type={showPwd ? "text" : "password"} hasError={!!errU.password}
                      value={usr.password} style={{ paddingRight: "36px" }}
                      onChange={(e) => setUsr({ ...usr, password: e.target.value })}
                      placeholder="Mínimo 8 caracteres"
                    />
                    <button type="button" onClick={() => setShowPwd(!showPwd)} style={{
                      position: "absolute", right: "4px", top: "50%", transform: "translateY(-50%)",
                      background: "none", border: "none", color: "#94A3B8", cursor: "pointer", display: "flex", padding: "4px"
                    }}>
                      {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <FieldError msg={errU.password} />
                </div>

                <div style={{ marginBottom: "24px" }}>
                  <label style={{ fontSize: "11px", fontWeight: "700", color: "#94A3B8", letterSpacing: "0.8px", textTransform: "uppercase" }}>Confirmar contraseña</label>
                  <UInput
                    type={showPwd ? "text" : "password"} hasError={!!errU.password_confirmation}
                    value={usr.password_confirmation}
                    onChange={(e) => setUsr({ ...usr, password_confirmation: e.target.value })}
                    placeholder="Repite la contraseña"
                  />
                  <FieldError msg={errU.password_confirmation} />
                </div>

                <div style={{ display: "flex", gap: "10px" }}>
                  <button type="button" onClick={() => setStep(1)} style={{
                    height: "46px", padding: "0 18px",
                    background: "#F8FAFC", color: "#64748B",
                    border: "2px solid #E2E8F0", borderRadius: "10px",
                    fontSize: "14px", fontWeight: "600", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: "6px",
                    fontFamily: "inherit", flexShrink: 0
                  }}>
                    <ChevronLeft size={16} /> Volver
                  </button>
                  <button type="submit" disabled={loading} style={{
                    flex: 1, height: "46px",
                    background: loading ? "#CBD5E1" : "#0F172A",
                    color: "white", border: "none", borderRadius: "10px",
                    fontSize: "14px", fontWeight: "700",
                    cursor: loading ? "not-allowed" : "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                    fontFamily: "inherit", transition: "background 0.15s", letterSpacing: "-0.2px"
                  }}
                    onMouseEnter={(e) => !loading && (e.currentTarget.style.background = "#1E2947")}
                    onMouseLeave={(e) => !loading && (e.currentTarget.style.background = loading ? "#CBD5E1" : "#0F172A")}
                  >
                    {loading ? (
                      <>
                        <span style={{ width: "16px", height: "16px", border: "2px solid rgba(255,255,255,0.3)", borderTop: "2px solid white", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                        Creando cuenta...
                      </>
                    ) : "Crear cuenta"}
                  </button>
                </div>

                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </form>
            </>
          )}
        </div>
      </div>

      <style>{`
        @media (max-width: 860px) {
          .tn-auth-left { display: none !important; }
        }
      `}</style>
    </div>
  );
}

export default RegisterForm;
